import React, { useEffect, useState } from "react";
import {
  Button,
  Box,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Checkbox,
  IconButton,
} from "@mui/material";
import axios from "axios";
import { BACKEND_BASE_URL } from "../config";
import { toast, ToastContainer } from "react-toastify";
import he from "he";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const statusColor = {
  open: "default",
  in_progress: "info",
  failed: "error",
  escalated: "error",
  scrape_failure: "warning",
};

const statusLabels = {
  open: "Open",
  in_progress: "In progress",
  failed: "Failed",
  escalated: "Escalated",
  scrape_failure: "Scrape failed",
};

function DetailCell({ text }) {
  const value = text || "—";
  return (
    <Tooltip title={value} placement="top-start" arrow enterDelay={300}>
      <Box
        sx={{
          maxWidth: "380px",
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: 1.4,
        }}
      >
        {value}
      </Box>
    </Tooltip>
  );
}

const ManageVideoSummaries = () => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({
    total: 0,
    open: 0,
    in_progress: 0,
    failed: 0,
    escalated: 0,
    scrape_failure: 0,
  });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [selected, setSelected] = useState([]);

  const fetchQueue = async (
    nextPage = page,
    nextRowsPerPage = rowsPerPage,
    nextSearch = searchQuery,
    nextStatus = statusFilter
  ) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_BASE_URL}/api/video-summaries/recovery-queue/`,
        {
          params: {
            page: nextPage + 1,
            page_size: nextRowsPerPage,
            q: nextSearch,
            status: nextStatus,
          },
        }
      );
      setItems(response.data?.items || []);
      setCounts(
        response.data?.counts || {
          total: 0,
          open: 0,
          in_progress: 0,
          failed: 0,
          escalated: 0,
          scrape_failure: 0,
        }
      );
      setTotalItems(response.data?.total_items || 0);
      setSelected([]);
    } catch (error) {
      console.error("Error fetching summary recovery queue:", error);
      toast.error("Failed to load summary status.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchQueue(0, rowsPerPage, searchQuery, statusFilter);
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, rowsPerPage]);

  const runRecovery = async () => {
    setRunning(true);
    try {
      await axios.post(`${BACKEND_BASE_URL}/api/video-summaries/recovery-run/`, {
        max_items: 1000,
      });
      toast.success("Recovery task queued. Refreshing queue...");
      setTimeout(() => fetchQueue(page, rowsPerPage, searchQuery, statusFilter), 1500);
    } catch (error) {
      console.error("Error queuing recovery task:", error);
      toast.error("Failed to start recovery task.");
    } finally {
      setRunning(false);
    }
  };

  const dismissItems = async (videoIds, dismissAll = false) => {
    setDismissing(true);
    try {
      const payload = dismissAll ? { all: true } : { video_ids: videoIds };
      const response = await axios.post(
        `${BACKEND_BASE_URL}/api/video-summaries/recovery-dismiss/`,
        payload
      );
      toast.success(response.data?.message || "Cleared item(s).");
      fetchQueue(page, rowsPerPage, searchQuery, statusFilter);
    } catch (error) {
      console.error("Error dismissing items:", error);
      toast.error("Failed to clear item(s).");
    } finally {
      setDismissing(false);
    }
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    fetchQueue(newPage, rowsPerPage, searchQuery, statusFilter);
  };

  const handleRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(0);
  };

  const toggleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(items.map((item) => item.video_id));
    } else {
      setSelected([]);
    }
  };

  const toggleSelect = (videoId) => {
    setSelected((prev) =>
      prev.includes(videoId) ? prev.filter((id) => id !== videoId) : [...prev, videoId]
    );
  };

  const displayStatus = (item) => {
    if (item.issue_type === "scrape_failure") {
      return "scrape_failure";
    }
    return item.status;
  };

  const displayStatusLabel = (item) => {
    const key = displayStatus(item);
    return statusLabels[key] || key;
  };

  return (
    <>
      <ToastContainer />
      <Grid container spacing={4}>
        <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography style={{ fontWeight: "bold" }} variant="h4">
              Summary Status
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
              Actionable scrape failures and missing summaries only. Resolved items are removed
              automatically.
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" flexWrap="nowrap">
            <TextField
              placeholder="Search..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ marginRight: "10px", minWidth: "220px", backgroundColor: "#fff" }}
              InputProps={{
                startAdornment: (
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    style={{ marginRight: "8px", color: "#555" }}
                  />
                ),
              }}
            />
            <FormControl
              size="small"
              style={{ minWidth: "140px", marginRight: "10px", backgroundColor: "#fff" }}
            >
              <InputLabel id="summary-status-filter-label">Status</InputLabel>
              <Select
                labelId="summary-status-filter-label"
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="in_progress">In progress</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="escalated">Escalated</MenuItem>
                <MenuItem value="scrape_failure">Scrape failed</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              style={{ marginRight: "10px", whiteSpace: "nowrap" }}
              onClick={() => fetchQueue(page, rowsPerPage, searchQuery, statusFilter)}
              disabled={loading}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              style={{ backgroundColor: "#0B194A", color: "white", whiteSpace: "nowrap" }}
              onClick={runRecovery}
              disabled={loading || running}
            >
              {running ? "Running..." : "Run Recovery"}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" gap={1} marginBottom={2} flexWrap="wrap" alignItems="center">
            <Chip label={`Total: ${counts.total || 0}`} />
            <Chip label={`Open: ${counts.open || 0}`} />
            <Chip label={`In progress: ${counts.in_progress || 0}`} color="info" />
            <Chip label={`Failed: ${counts.failed || 0}`} color="error" />
            <Chip label={`Escalated: ${counts.escalated || 0}`} color="error" variant="outlined" />
            <Chip
              label={`Scrape failed: ${counts.scrape_failure || 0}`}
              color="warning"
            />
            {selected.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={dismissing}
                onClick={() => dismissItems(selected)}
                startIcon={<DeleteOutlineIcon />}
              >
                Clear selected ({selected.length})
              </Button>
            )}
            {(counts.total || 0) > 0 && (
              <Button
                size="small"
                variant="text"
                color="error"
                disabled={dismissing}
                onClick={() => dismissItems([], true)}
              >
                Clear all
              </Button>
            )}
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Typography variant="h6">No items need attention.</Typography>
          ) : (
            <Box sx={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "4px" }}>
              <Table size="small" style={{ minWidth: "1200px", backgroundColor: "#fff" }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding="checkbox"
                      style={{
                        backgroundColor: "#152F8F",
                        color: "#fff",
                        border: "1px solid #ddd",
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={items.length > 0 && selected.length === items.length}
                        indeterminate={selected.length > 0 && selected.length < items.length}
                        onChange={toggleSelectAll}
                        sx={{ color: "#fff", "&.Mui-checked": { color: "#fff" } }}
                      />
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#152F8F",
                        color: "#fff",
                        fontWeight: "bold",
                        border: "1px solid #ddd",
                      }}
                    >
                      Title
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#152F8F",
                        color: "#fff",
                        fontWeight: "bold",
                        border: "1px solid #ddd",
                      }}
                    >
                      Video ID
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#152F8F",
                        color: "#fff",
                        fontWeight: "bold",
                        border: "1px solid #ddd",
                      }}
                    >
                      Location
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#152F8F",
                        color: "#fff",
                        fontWeight: "bold",
                        border: "1px solid #ddd",
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#152F8F",
                        color: "#fff",
                        fontWeight: "bold",
                        border: "1px solid #ddd",
                      }}
                    >
                      Failures
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#152F8F",
                        color: "#fff",
                        fontWeight: "bold",
                        border: "1px solid #ddd",
                      }}
                    >
                      Last Attempt
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#152F8F",
                        color: "#fff",
                        fontWeight: "bold",
                        border: "1px solid #ddd",
                      }}
                    >
                      Detail
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#152F8F",
                        color: "#fff",
                        fontWeight: "bold",
                        border: "1px solid #ddd",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.video_id} hover selected={selected.includes(item.video_id)}>
                      <TableCell padding="checkbox" style={{ border: "1px solid #ddd" }}>
                        <Checkbox
                          size="small"
                          checked={selected.includes(item.video_id)}
                          onChange={() => toggleSelect(item.video_id)}
                        />
                      </TableCell>
                      <TableCell style={{ border: "1px solid #ddd" }}>
                        {he.decode(item.title || item.video_id)}
                      </TableCell>
                      <TableCell style={{ border: "1px solid #ddd" }}>{item.video_id}</TableCell>
                      <TableCell style={{ border: "1px solid #ddd" }}>
                        {item.state} / {item.county} / {item.municipality}
                      </TableCell>
                      <TableCell style={{ border: "1px solid #ddd" }}>
                        <Chip
                          size="small"
                          label={displayStatusLabel(item)}
                          color={statusColor[displayStatus(item)] || "default"}
                        />
                      </TableCell>
                      <TableCell style={{ border: "1px solid #ddd" }}>
                        {item.failure_count || 0}
                      </TableCell>
                      <TableCell style={{ border: "1px solid #ddd" }}>
                        {item.last_attempt_at
                          ? new Date(item.last_attempt_at).toLocaleString()
                          : "—"}
                      </TableCell>
                      <TableCell style={{ border: "1px solid #ddd" }}>
                        <DetailCell text={item.last_error} />
                      </TableCell>
                      <TableCell style={{ border: "1px solid #ddd" }}>
                        <Tooltip title="Clear from Summary Status">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={dismissing}
                            onClick={() => dismissItems([item.video_id])}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalItems}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[25, 50, 100, 200]}
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default ManageVideoSummaries;
